import { useEffect, useMemo, useState } from 'react';
import { Info, Plus } from 'lucide-react';
import { Button, InfoAlert } from '@/components/ui';
import { SenderCombobox } from '@/components/domain/shared/SenderCombobox';
import { COMMON_SENDER_PLATFORMS } from '@/constants/senderPlatforms';
import { CarouselHeader } from './CarouselHeader';
import { WalkthroughSenderList } from './WalkthroughSenderList';
import type { WalkthroughSenderListItem } from './WalkthroughSenderList';
import { validateSender } from '@/lib/senderHelpers';

export interface Step1CheckSendersProps {
  /** Senders currently saved in the DB (the source of truth before any edit). */
  senders: WalkthroughSenderListItem[];
  onConfirm: () => void;
  /**
   * Tapping X (or backdrop/Escape on the carousel). The parent pops the
   * LeaveWithoutSaving confirm whenever the user is in the Edit Senders
   * sub-state (YAR-238 AC 10), so Step 1 reports that via
   * `onCloseRequest({ editing })`.
   */
  onCloseRequest: (info: { editing: boolean }) => void;
  /**
   * Notifies the parent when the Edit Senders sub-state toggles, so the
   * carousel's own backdrop/Escape close can also route through the
   * LeaveWithoutSaving confirm while editing (AC 10).
   */
  onEditStateChange?: (editing: boolean) => void;
  /**
   * Batch persist: called when the user taps "Save changes" inside the
   * Edit Senders sub-state. The handler is responsible for inserting the
   * adds, deleting the removals, and resolving the new on-disk list,
   * then resolving the promise. Errors should surface via toast on the
   * parent side; this component just awaits resolution and exits Edit.
   */
  onSaveSenders: (next: WalkthroughSenderListItem[]) => Promise<void>;
}

type SubState = 'default' | 'edit';

/**
 * Step 1 of the YAR-238 walkthrough — "Check your senders".
 *
 * Sub-states (selected internally; the carousel always renders as
 * "1 of 3"):
 *   - `default`: read-only list (or empty illustration) + "Confirm
 *     senders" + "Edit senders" link.
 *   - `edit`:    inline edit with trash icons + add input + "Save
 *     changes" / "Cancel". All mutations are in-memory until Save.
 *
 * Empty-state semantics (AC 6, 7, 9):
 *   - In default mode with zero saved senders → Confirm is disabled +
 *     info alert reads "Add at least one sender to continue."
 *   - In edit mode the same rule applies to "Save changes": you can't
 *     save zero senders.
 */
export function Step1CheckSenders({
  senders,
  onConfirm,
  onCloseRequest,
  onEditStateChange,
  onSaveSenders,
}: Step1CheckSendersProps) {
  const [subState, setSubState] = useState<SubState>('default');
  const [draft, setDraft] = useState<WalkthroughSenderListItem[]>(senders);
  const [newValue, setNewValue] = useState('');
  const [inputError, setInputError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const enterEdit = () => {
    setDraft(senders);
    setNewValue('');
    setInputError(undefined);
    setSubState('edit');
  };

  const cancelEdit = () => {
    setDraft(senders);
    setNewValue('');
    setInputError(undefined);
    setSubState('default');
  };

  const handleRemove = (id: string) => {
    setDraft((current) => current.filter((s) => s.id !== id));
  };

  const handleAdd = () => {
    const validation = validateSender(newValue);
    if (!validation.valid || !validation.storageValue) {
      setInputError(validation.error);
      return;
    }
    // Pin the narrowed string into a const so TS doesn't lose the
    // narrowing across the setDraft callback boundary.
    const storageValue = validation.storageValue;
    const exists = draft.some(
      (s) =>
        s.type === validation.type &&
        s.value.toLowerCase() === storageValue.toLowerCase(),
    );
    if (exists) {
      setInputError('This sender has already been added');
      return;
    }
    setDraft((current) => [
      ...current,
      {
        // Optimistic temp id — the real id comes back from the server on
        // Save. Walkthrough never persists this id so collision risk is
        // bounded to the in-memory list.
        id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: validation.type,
        value: storageValue,
      },
    ]);
    setNewValue('');
    setInputError(undefined);
  };

  // System-managed rows (the user's own forwarding address) don't count
  // toward the "at least one sender" gate — that requirement exists so
  // the user has actually picked senders Yaruta should accept email from,
  // and the OAuth-seeded row is automatic, not a real approval.
  const userPickedCount = (list: WalkthroughSenderListItem[]) =>
    list.filter((s) => !s.required).length;

  const handleSave = async () => {
    // AC 9: at least one user-picked sender required to save.
    if (userPickedCount(draft) === 0) return;
    setSaving(true);
    try {
      await onSaveSenders(draft);
      setSubState('default');
    } finally {
      setSaving(false);
    }
  };

  // AC 10: being in the Edit Senders sub-state is what gates the
  // LeaveWithoutSaving confirm — tapping X while editing always prompts,
  // regardless of whether anything was actually changed.
  useEffect(() => {
    onEditStateChange?.(subState === 'edit');
  }, [subState, onEditStateChange]);

  const handleClose = () => {
    onCloseRequest({ editing: subState === 'edit' });
  };

  // Type-ahead options for the add-sender field: common platforms the draft
  // doesn't already include. No live scan in the walkthrough — this curated
  // list is the permission-free way to speed up adding a platform.
  const senderOptions = useMemo(() => {
    const have = new Set(
      draft
        .filter((s) => s.type === 'domain')
        .map((s) => s.value.toLowerCase()),
    );
    return COMMON_SENDER_PLATFORMS.filter((o) => {
      const host = (typeof o === 'string' ? o : o.value)
        .replace(/^@/, '')
        .toLowerCase();
      return !have.has(host);
    });
  }, [draft]);

  const list = subState === 'edit' ? draft : senders;
  // "Empty" from the user's POV = no senders the user picked. The
  // system_managed forwarding row alone is still effectively empty.
  const isEmpty = userPickedCount(list) === 0;

  return (
    <div className="flex flex-col gap-4 p-5 max-h-[calc(85dvh)] overflow-y-auto">
      <CarouselHeader
        stepIndex={1}
        onClose={handleClose}
      />

      <div className="flex flex-col gap-2">
        <h2 className="text-xl font-semibold text-foreground">
          Check your senders
        </h2>
        <p className="text-base text-foreground">
          Yaruta only reviews emails from senders you&apos;ve approved. Make
          sure the right ones are on this list before continuing.
        </p>
      </div>

      <WalkthroughSenderList
        items={list}
        onRemove={subState === 'edit' ? handleRemove : undefined}
      />

      {subState === 'edit' && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-foreground">
            Add sender (domain or email address)
          </label>
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <SenderCombobox
                value={newValue}
                onChange={(next) => {
                  setNewValue(next);
                  if (inputError) setInputError(undefined);
                }}
                onSubmit={handleAdd}
                options={senderOptions}
                placeholder="e.g. @school.org or teacher@email.com"
                ariaLabel="Add sender (domain or email address)"
              />
            </div>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!newValue.trim()}
              aria-label="Add sender"
              // `self-stretch` (no fixed height) makes the button match the
              // Input's rendered height so their bottom edges align — a fixed
              // `h-10` left the button 2px taller than the ~38px input box.
              className="w-10 self-stretch flex items-center justify-center rounded-md border border-border disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <Plus
                size={20}
                className="text-icon-default"
              />
            </button>
          </div>
          {inputError && <p className="text-xs text-danger">{inputError}</p>}
        </div>
      )}

      {/* AC 7 + AC 9: the helper renders whenever there are no user-picked
          senders — in default mode AND while editing (e.g. the parent
          removed the last sender, leaving only the system_managed row). */}
      {isEmpty && (
        <InfoAlert icon={<Info size={16} />}>
          Add at least one sender to continue.
        </InfoAlert>
      )}

      <div className="flex flex-col gap-3 mt-2">
        {subState === 'default' ? (
          <>
            <Button
              variant="primary"
              fullWidth
              onClick={onConfirm}
              disabled={isEmpty}
            >
              Confirm senders
            </Button>
            <button
              type="button"
              onClick={enterEdit}
              className="w-full text-foreground font-semibold text-base leading-6 py-1.5 px-2 cursor-pointer"
            >
              Edit senders
            </button>
          </>
        ) : (
          <>
            <Button
              variant="primary"
              fullWidth
              onClick={handleSave}
              // AC 9: at least one user-picked sender is required — the
              // system_managed Required row alone doesn't count.
              disabled={saving || userPickedCount(draft) === 0}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="w-full text-foreground font-semibold text-base leading-6 py-1.5 px-2 cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
